import {Injectable} from '@angular/core'
import {TerminalEvent} from '../interfaces/terminal-event'
import * as listConfig from './config/TerminalEventsFieldDefinitionsByType_list.json'
import * as detailConfig from './config/TerminalEventsFieldDefinitionsByType_detail.json'
import * as syncEventListConfig from './config/SyncEventsFieldDefinitionsByType_list.json'
import * as syncEventDetailConfig from './config/SyncEventsFieldDefinitionsByType_detail.json'
import {
  detailViewRow,
  detailViewSegment,
} from '../components/terminal-events-detail/terminal-events-detail.component'
import {mapAuthResponseToText} from '@app/features/terminal-park/transactions/auth-responses'

export interface ListViewPreference {
  attribute: string
  label: string | false
  showMissing?: boolean
  missingText?: string
}

export interface DynamicRowItem {
  value: string
  sourceName?: string | null
  sourceField: string | null
  classes?: string // text-success text-danger text-warning etc
  colspan?: number
  [key: string]: any
}

export interface DetailViewRecord {
  attribute: string
  label: string
}

export interface DetailViewSegmentConfig {
  title: string
  data: DetailViewRecord[]
}

export interface DetailViewTypeConfig {
  remainder?: {title: string; enable: boolean}
  segments: DetailViewSegmentConfig[]
}

export interface DetailViewConfig {
  [key: string]: DetailViewTypeConfig
}

@Injectable({
  providedIn: 'root',
})
export class TerminalEventsListConfigurationService {
  showDetailRemainders = true

  constructor() {}

  // DetailView Config
  createDetailViewFromConfig(
    terminalEvent: TerminalEvent,
    config = {},
    selected = {},
  ): Array<detailViewSegment> {
    let combined = []
    let segments = [config['primary']]
    if (Object.keys(config).length !== 0) {
      let excludedRemainder = {}
      segments.forEach((segment) => {
        if (!segment) {
          console.warn('A configuration error has been prevented..')
          return
        }
        let combinedSegment: detailViewSegment = {
          title: segment.label,
          data: [],
        }
        Object.keys(segment.data).forEach((keyName) => {
          if (!terminalEvent.hasOwnProperty(keyName)) {
            return
          }
          combinedSegment.data.push(
            this.createDetailViewRow(
              terminalEvent,
              keyName,
              segment.data[keyName],
              selected,
            ),
          )
        })
        const respCodeWithIndex = combinedSegment.data.reduce(
          (acc, segment, index) => {
            if (segment.key === 'TXN-Authorisation Response Code') {
              acc.element = segment
              acc.index = index
            }
            return acc
          },
          {element: null, index: -1},
        )

        if (respCodeWithIndex.element) {
          const authResponseMessage: detailViewRow = {
            key: 'TXN-Authorisation Response Message',
            label: 'Authorisation Response Message',
            value: mapAuthResponseToText(respCodeWithIndex.element.value),
            selected: respCodeWithIndex.element.selected,
            ingestSequence: respCodeWithIndex.element.ingestSequence,
          }
          combinedSegment.data.splice(
            respCodeWithIndex.index + 1,
            0,
            authResponseMessage,
          )
        }
        combined.push(combinedSegment)
      })
      // show remaining
      if (this.showDetailRemainders) {
        let combinedSegment: detailViewSegment = {
          title: 'Other fields',
          data: [],
        }
        // general first
        if (config['general'].hasOwnProperty('others')) {
          Object.keys(config['general'].others.data).forEach((key) => {
            let conf = config['general'].others.data[key]
            if (conf.Hidden) {
              excludedRemainder[key] = true
              return
            }
            if (terminalEvent.hasOwnProperty(key)) {
              combinedSegment.data.push(
                this.createDetailViewRow(terminalEvent, key, conf, selected),
              )
              excludedRemainder[key] = true
            }
          })
        }

        // remaining
        Object.keys(terminalEvent).forEach((key) => {
          if (excludedRemainder[key]) {
            return
          }
          if (!config.hasOwnProperty('others')) {
            return
          }
          let conf = config['others'].data.hasOwnProperty(key)
            ? config['others'].data[key]
            : {}
          if (conf.hasOwnProperty('Hidden') && conf.Hidden === 1) {
            return
          }
          if (terminalEvent.hasOwnProperty(key)) {
            combinedSegment.data.push(
              this.createDetailViewRow(terminalEvent, key, conf, selected),
            )
          }
        })
        combined.push(combinedSegment)
      }
    }
    return combined
  }

  createDetailViewRow(terminalEvent, key, config, selected) {
    return {
      label: config.hasOwnProperty('Label') ? config.Label : key,
      key: key,
      value: terminalEvent[key],
      selected:
        selected.hasOwnProperty(key + ': ' + terminalEvent[key].toString()) &&
        selected[key + ': ' + terminalEvent[key].toString()] ===
          terminalEvent[key],
      ingestSequence: terminalEvent.ingestSequence,
    }
  }

  getDetailViewConfig(
    terminalEvent: TerminalEvent,
    selected = {},
    fieldDefinitionFile = 'TerminalEventsFieldDefinitionsByType_detail.json',
    filterKey = 'sourceName',
  ): detailViewSegment[] {
    let detailView = []
    let config = {}
    let detailViewConfig
    if (
      fieldDefinitionFile === 'SyncEventsFieldDefinitionsByType_detail.json'
    ) {
      detailViewConfig = syncEventDetailConfig
    } else {
      detailViewConfig = detailConfig
    }
    if (detailViewConfig['default'].hasOwnProperty(terminalEvent[filterKey])) {
      config = detailViewConfig['default'][terminalEvent[filterKey]]
    }
    config['general'] = {}
    if (detailViewConfig['default'].hasOwnProperty('general')) {
      // config['general'] = detailViewConfig['default']['general'];
    }
    detailView = this.createDetailViewFromConfig(
      terminalEvent,
      config,
      selected,
    )

    if (
      typeof this['getDetailViewConfig_' + [terminalEvent[filterKey]]] ===
      'function'
    ) {
      return this['getDetailViewConfig_' + [terminalEvent[filterKey]]](
        terminalEvent,
        detailView,
      )
    }
    return detailView
  }

  // Custom detailView configuration
  getDetailViewConfig_APP(terminalEvent, detailView): detailViewSegment[] {
    return detailView
    return detailView.map((segment) => {
      return segment.data.map((item) => {
        if (item.value === 'BKM') {
          item.value = 'BKM has been overwritten'
        }
        return item
      })
    })
  }

  // List config
  createRowFromConfig(
    terminalEvent: TerminalEvent,
    config: {},
    filterKey,
  ): Array<DynamicRowItem> {
    return Object.keys(config)
      .map((key) => {
        return this.createListCell(terminalEvent, key, config, filterKey)
      })
      .filter((res) => {
        return res !== undefined
      })
  }

  createListCell(terminalEvent, key, config, filterKey) {
    if (config[key].hasOwnProperty('Hidden') && config[key].Hidden === true) {
      return
    }
    let value = ''
    let detailValue = ''
    if (!terminalEvent.hasOwnProperty(key)) {
      if (
        config[key].hasOwnProperty('Show missing') &&
        config[key]['Show missing'] === true
      ) {
        // continue
      } else {
        // skip
        return
      }
    } else {
      value = terminalEvent[key]
      if (
        config[key].hasOwnProperty('Overview Label') &&
        config[key]['Overview Label'] === true
      ) {
        let label = key
        if (config[key].hasOwnProperty('Label')) {
          label = config[key].Label
        }
        value = `${label}: ${terminalEvent[key]}`
      }
      if (
        config[key].hasOwnProperty('Overview Detail') &&
        config[key]['Overview Detail'] === true
      ) {
        detailValue = terminalEvent[key]
        if (
          config[key].hasOwnProperty('Overview Detail Label') &&
          config[key]['Overview Detail Label'] === true
        ) {
          let label = key
          if (config[key].hasOwnProperty('Label')) {
            label = config[key].Label
          }
          detailValue = `${label}: ${detailValue}`
        }
      }
    }
    return {
      value: value,
      [filterKey]: terminalEvent[filterKey],
      sourceField: key,
      classes: config[key].hasOwnProperty('Class') ? config[key]['Class'] : '',
      type: config[key].hasOwnProperty('Type') ? config[key]['Type'] : '',
      overview: config[key].hasOwnProperty('Overview')
        ? config[key]['Overview']
        : '',
      overviewDetail: config[key].hasOwnProperty('Overview Detail')
        ? config[key]['Overview Detail']
        : '',
      detailValue: detailValue,
    }
  }

  getListViewConfig(
    terminalEvent: TerminalEvent,
    fieldDefinitionFile = 'TerminalEventsFieldDefinitionsByType_list.json',
    filterKey = 'sourceName',
  ): {dynamicColumns: DynamicRowItem[]; detailData: string[]} {
    let config
    if (fieldDefinitionFile === 'SyncEventsFieldDefinitionsByType_list.json') {
      config = syncEventListConfig
    } else {
      config = listConfig
    }

    let row = []
    let detailData = []
    if (config['default'].hasOwnProperty(terminalEvent[filterKey])) {
      row = this.createRowFromConfig(
        terminalEvent,
        config['default'][terminalEvent[filterKey]],
        filterKey,
      )
      detailData = row.filter((item) => {
        return item.overviewDetail
      })
    } else if (config['default'].hasOwnProperty('ANY')) {
      row = this.createRowFromConfig(
        terminalEvent,
        config['default']['ANY'],
        filterKey,
      )
      detailData = row.filter((item) => {
        return item.overviewDetail
      })
    }
    row = row.filter((item) => {
      return item.overview
    })

    if (
      typeof this['getListViewConfig_' + terminalEvent[filterKey]] ===
      'function'
    ) {
      return this['getListViewConfig_' + terminalEvent[filterKey]](
        terminalEvent,
        row,
      )
    }

    if (row.length) {
      return {dynamicColumns: row, detailData: detailData}
    }

    // fallthrough max 4 items in row:
    let result = []
    let count = 0
    Object.keys(terminalEvent).forEach((eventKey) => {
      if (count < 4) {
        result.push({
          value: terminalEvent[eventKey],
          [filterKey]: terminalEvent[filterKey],
          classes: '',
        })
      }
      count++
    })
    return {dynamicColumns: result, detailData: []}
  }

  // Custom listview configuration
  // getListViewConfig_BOOT(terminalEvent: TerminalEvent, row: DynamicRowItem[]):Array<DynamicRowItem> {
  //   // for example filter all '0000' values
  //   // return row.filter(rowItem => {
  //   //   return rowItem.value !== '0000'
  //   // })
  //   return row.filter(rowItem => {
  //     return rowItem.sourceField !== 'BOOT-DD' && rowItem.value !== '0000'
  //   })
  // }

  // getListViewConfig_GPRS(terminalEvent: TerminalEvent, row: DynamicRowItem[]):Array<DynamicRowItem> {
  //   return row.filter(rowItem => {
  //     return rowItem.sourceName !== 'GPRS-Error' && rowItem.value !== '0000'
  //   })
  // }
}
